/**
 * Aurora (порт React Bits, без зависимости `ogl`): полосы северного сияния
 * из шума симплекса, окрашенные градиентом из трёх цветов.
 *
 * Бережём батарею и 3D-мозг: рисуем в половинном разрешении (сияние всё
 * равно размытое), не чаще 30 кадров в секунду, на скрытой вкладке не
 * рисуем вовсе. `paused` замирает на текущем кадре — так фон не делит
 * видеокарту с мозгом на карте. При `prefers-reduced-motion` — один кадр.
 */

import { useEffect, useRef } from 'react';

interface AuroraGLProps {
  /** Три цвета слева направо, `#RRGGBB`. */
  readonly colorStops: readonly [string, string, string];
  readonly amplitude?: number;
  readonly blend?: number;
  readonly speed?: number;
  readonly paused?: boolean;
  readonly className?: string;
}

const VERTEX = `#version 300 es
in vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }`;

const FRAGMENT = `#version 300 es
precision highp float;
uniform float uTime;
uniform float uAmplitude;
uniform vec3 uColorStops[3];
uniform vec2 uResolution;
uniform float uBlend;
out vec4 fragColor;

vec3 permute(vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

vec3 ramp(float t) {
  return t < 0.5
    ? mix(uColorStops[0], uColorStops[1], t * 2.0)
    : mix(uColorStops[1], uColorStops[2], (t - 0.5) * 2.0);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec3 rampColor = ramp(uv.x);
  float height = snoise(vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)) * 0.5 * uAmplitude;
  height = exp(height);
  height = (uv.y * 2.0 - height + 0.2);
  float intensity = 0.6 * height;
  float alpha = smoothstep(0.2 - uBlend * 0.5, 0.2 + uBlend * 0.5, intensity);
  // Чистый цвет полосы (premultiplied): на светлом фоне сияние не «грязнит».
  float a = alpha * clamp(intensity, 0.0, 1.0);
  fragColor = vec4(rampColor * a, a);
}`;

const FRAME_MS = 1000 / 30;
const RESOLUTION_SCALE = 0.5;

function hexToRgb(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (shader === null) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return gl.getShaderParameter(shader, gl.COMPILE_STATUS) === true ? shader : null;
}

export function AuroraGL({ colorStops, amplitude = 1.0, blend = 0.5, speed = 1.0, paused = false, className }: AuroraGLProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Пауза читается из ref: смена маршрута не пересоздаёт WebGL-контекст.
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const [c0, c1, c2] = colorStops;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const gl = canvas.getContext('webgl2', { premultipliedAlpha: true, alpha: true, antialias: false });
    // Без WebGL2 остаётся спокойный CSS-фон под холстом.
    if (gl === null) return;

    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (vertex === null || fragment === null || program === null) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) return;
    gl.useProgram(program);

    // Один треугольник на весь экран.
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    const uTime = gl.getUniformLocation(program, 'uTime');
    const uResolution = gl.getUniformLocation(program, 'uResolution');
    gl.uniform1f(gl.getUniformLocation(program, 'uAmplitude'), amplitude);
    gl.uniform1f(gl.getUniformLocation(program, 'uBlend'), blend);
    gl.uniform3fv(gl.getUniformLocation(program, 'uColorStops'), [...hexToRgb(c0), ...hexToRgb(c1), ...hexToRgb(c2)]);

    const resize = () => {
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * RESOLUTION_SCALE));
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * RESOLUTION_SCALE));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uResolution, canvas.width, canvas.height);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const draw = (time: number) => {
      gl.uniform1f(uTime, time * 0.001 * speed * 0.5);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let last = 0;
    const loop = (time: number) => {
      frame = requestAnimationFrame(loop);
      if (pausedRef.current || document.hidden || time - last < FRAME_MS) return;
      last = time;
      draw(time);
    };
    draw(performance.now());
    if (!reduceMotion) frame = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    };
  }, [amplitude, blend, speed, c0, c1, c2]);

  return <canvas ref={canvasRef} aria-hidden="true" className={['block h-full w-full', className ?? ''].join(' ')} />;
}
